/**
 * Type definitions and error class for Anthropic API client
 */

export class AnthropicApiError extends Error {
  status: number;
  retryAfterMs: number | null;

  constructor(message: string, status: number, retryAfterMs: number | null) {
    super(message);
    this.name = 'AnthropicApiError';
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }
}

export interface AnthropicModelPricing {
  baseInput: number;
  cacheRead: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
  output: number;
  cacheWriteDefault?: number;
  serverToolInput?: number;
  webSearch?: number;
  codeExecution?: number;
}

export interface AnthropicUsageData {
  timestamp: string;
  model?: string;
  workspace_id?: string;
  api_key_id?: string;
  account_id?: string | null;
  service_account_id?: string | null;
  service_tier?: string;
  context_window?: string;
  inference_geo?: 'global' | 'us' | 'not_available' | string | null;
  speed?: 'standard' | 'fast' | string;
  uncached_input_tokens?: number;
  cache_read_input_tokens?: number;
  cache_creation?: {
    input_tokens?: number;
    ephemeral_1h_input_tokens?: number;
    ephemeral_5m_input_tokens?: number;
  };
  output_tokens?: number;
  server_tool_use?: {
    input_tokens?: number;
    web_search_requests?: number;
  };
  web_search_uses?: number;
}

export interface AnthropicUsageBucket {
  starting_at: string;
  ending_at: string;
  results: AnthropicUsageData[];
}

/** A single cost item within a cost_report bucket's results array */
export interface AnthropicCostResultItem {
  amount: string;
  currency: string;
  cost_type: 'tokens' | 'web_search' | 'code_execution' | 'session_usage' | string;
  description?: string | null;
  model?: string | null;
  workspace_id?: string | null;
  service_tier?: 'standard' | 'batch' | null;
  context_window?: '0-200k' | '200k-1M' | null;
  token_type?: string | null;
  inference_geo?: string | null;
}

/** A time bucket returned by the cost_report endpoint */
export interface AnthropicCostBucket {
  starting_at: string;
  ending_at: string;
  results: AnthropicCostResultItem[];
}

export interface AnthropicUsageResponse {
  data: AnthropicUsageBucket[];
  has_more: boolean;
  next_page?: string;
}

export interface AnthropicCostResponse {
  data: AnthropicCostBucket[];
  has_more: boolean;
  next_page?: string;
}

export interface AnthropicApiKey {
  id: string;
  name: string;
  type?: string;
  workspace_id?: string | null;
  status?: 'active' | 'inactive' | 'archived';
  partial_key_hint?: string;
  created_at?: string;
  created_by?: { id: string; type: string };
}

export interface AnthropicWorkspace {
  id: string;
  name: string;
  description?: string | null;
  archived_at?: string | null;
  created_at?: string;
  status?: 'active' | 'archived';
}

export interface AnthropicApiKeysResponse {
  data: AnthropicApiKey[];
  has_more: boolean;
  first_id?: string;
  last_id?: string;
}

export interface AnthropicWorkspacesResponse {
  data: AnthropicWorkspace[];
  has_more: boolean;
  first_id?: string;
  last_id?: string;
}

export interface AnthropicUser {
  id: string;
  type: string;
  email: string;
  name: string;
  role?: string;
  added_at?: string;
}

export interface AnthropicUsersResponse {
  data: AnthropicUser[];
  has_more: boolean;
  first_id?: string;
  last_id?: string;
}

export interface AnthropicApiKeysParams {
  status?: 'active' | 'inactive' | 'archived';
  workspace_id?: string;
  limit?: number;
  after_id?: string;
}

export interface UsageReportParams {
  starting_at: string;
  ending_at: string;
  bucket_width?: '1m' | '1h' | '1d';
  group_by?: ('model' | 'workspace_id' | 'api_key_id' | 'service_tier' | 'context_window' | 'speed' | 'inference_geo' | 'account_id' | 'service_account_id')[];
  models?: string[];
  service_tiers?: ('standard' | 'batch' | 'priority' | 'priority_on_demand' | 'flex' | 'flex_discount')[];
  context_window?: string[];
  inference_geos?: ('global' | 'us' | 'not_available')[];
  speeds?: ('standard' | 'fast')[];
  limit?: number;
  page?: string;
}

export interface CostReportParams {
  starting_at: string;
  ending_at: string;
  group_by?: ('workspace_id' | 'description')[];
  limit?: number;
  page?: string;
}

export interface AnthropicTokenBreakdown {
  baseInput: number;
  cacheRead: number;
  cacheWrite5m: number;
  cacheWrite1h: number;
  cacheWriteOther: number;
  serverToolInput: number;
  outputTokens: number;
  totalInputTokens: number;
  totalTokens: number;
  webSearchRequests: number;
}

export interface AnthropicCostBreakdown {
  baseInputCost: number;
  cacheReadCost: number;
  cacheWrite5mCost: number;
  cacheWrite1hCost: number;
  cacheWriteOtherCost: number;
  serverToolCost: number;
  outputCost: number;
  webSearchCost: number;
  totalCost: number;
}
